import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import { 
  Users, Plus, Edit, Trash2, Shield, User, Loader2, 
  Building2, Gavel, Mail, Phone, Settings, FileText,
  MessageSquare, CheckCircle, XCircle, Send, Save,
  AlertTriangle, Search, Wrench, MapPin, History
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Skeleton } from "../components/ui/skeleton";
import DailyDigestPanel from "../components/DailyDigestPanel";
import PinakioBranchMappingTab from "../components/PinakioBranchMappingTab";
import BenchJudgeSyncCard from "../components/BenchJudgeSyncCard";
import WorkloadLimitCard from "../components/WorkloadLimitCard";
import ReassignmentDigestCard from "../components/ReassignmentDigestCard";
import AuditLogTab from "../components/AuditLogTab";
import LegalDocsCard from "../components/LegalDocsCard";
import SolonBulkCard from "../components/SolonBulkCard";
import DocLinksCard from "../components/DocLinksCard";
import CourtTypeFixCard from "../components/CourtTypeFixCard";
import PartyAnonCard from "../components/PartyAnonCard";
import { BranchAttributionCard } from "../components/BranchAttributionCard";
import { EakFixCard } from "../components/EakFixCard";
import DocketSettingsCard from "../components/DocketSettingsCard";
import PendingRegistrationsCard from "../components/PendingRegistrationsCard";
import { RoleScopeFields, ExtraRolesFields } from "../components/RoleScopeFields";
import { ROLE_COURT_TYPE, ROLE_BADGE } from "../components/roleConstants";
import { BranchFilterSelect } from "../components/Workspace";
import { toast } from "sonner";

const positionLabels = {
  secretary: "Δικαστικός/ή Γραμματέας",
  department_head: "Προϊστάμενος/η Τμήματος",
  judge: "Δικαστής",
  council_president: "Πρόεδρος Τριμελούς Συμβουλίου",
  council_member: "Μέλος Τριμελούς Συμβουλίου",
  // Νέοι ρόλοι κοινοποίησης
  pretrial_secretariat_monomeles: "Γραμματεία Προδικαστικού Μονομελούς",
  pretrial_secretariat_polymeles: "Γραμματεία Προδικαστικού Πολυμελούς",
  prelodge_secretariat_monomeles: "Γραμματεία Προκατάθεσης Μονομελούς",
  prelodge_secretariat_polymeles: "Γραμματεία Προκατάθεσης Πολυμελούς",
  coordinator: "Υπεύθυνος Συντονισμού",
};

const ALL_PAGES = [
  { id: "dashboard", label: "Πίνακας Ελέγχου", path: "/" },
  { id: "today", label: "Σήμερα", path: "/today" },
  { id: "cases", label: "Υποθέσεις", path: "/cases" },
  { id: "hearings", label: "Πινάκια", path: "/hearings" },
  { id: "dockets", label: "Εκθέματα SOLON", path: "/dockets" },
  { id: "bench-calendar", label: "Ημερολόγιο Έδρας", path: "/bench-calendar" },
  { id: "assignment-board", label: "Προσδιορισμός", path: "/assignment-board" },
  { id: "kpold", label: "ΚΠολΔ", path: "/kpold" },
  { id: "pretrial", label: "Προδικαστικός", path: "/pretrial" },
  { id: "dispatch", label: "Διακίνηση", path: "/dispatch" },
  { id: "printouts", label: "Εκτυπώσεις", path: "/printouts" },
  { id: "import", label: "Εισαγωγή", path: "/import" },
  { id: "calendar", label: "Ημερολόγιο", path: "/calendar" },
  { id: "reports", label: "Αναφορές", path: "/reports" },
  { id: "statistics", label: "Στατιστικά", path: "/statistics" },
  { id: "correspondence", label: "Αλληλογραφία", path: "/correspondence" },
  { id: "messages", label: "Μηνύματα", path: "/messages" },
];

// ========== USERS TAB ==========
const UsersTab = () => {
  const [users, setUsers] = useState([]);
  const [availableBranches, setAvailableBranches] = useState(["Πρωτοδικείο Αθηνών"]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    username: "", password: "", full_name: "", title: "", email: "", phone: "",
    role: "user", position: "secretary", court_types: ["Μονομελές", "Πολυμελές"],
    court_branch: "Πρωτοδικείο Αθηνών", privacy_mode: false,
    allowed_pages: null, sector_numbers: []
  });

  useEffect(() => {
    fetchUsers();
    // Load available branches από pinakio mappings
    (async () => {
      try {
        const r = await axios.get(`${API}/admin/pinakio-branches`);
        const set = new Set(["Πρωτοδικείο Αθηνών"]);
        for (const m of (r.data.mappings || [])) {
          if (m.branch) set.add(m.branch);
        }
        for (const b of (r.data.branches || [])) {
          if (b) set.add(b);
        }
        setAvailableBranches([...set]);
      } catch { /* fallback default */ }
    })();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/users`);
      setUsers(response.data);
    } catch { toast.error("Σφάλμα φόρτωσης χρηστών"); }
    finally { setLoading(false); }
  };

  const handleOpenDialog = (user = null) => {
    if (user) {
      setEditingUser(user);
      setForm({
        username: user.username, password: "", full_name: user.full_name,
        title: user.title || "",
        email: user.email || "", phone: user.phone || "",
        role: user.role, position: user.position || "secretary",
        court_types: user.court_types || ["Μονομελές", "Πολυμελές"],
        court_branch: user.court_branch || "Πρωτοδικείο Αθηνών",
        privacy_mode: user.privacy_mode || false,
        allowed_pages: user.allowed_pages || null,
        sector_numbers: user.sector_numbers || [],
        composition_sector_number: user.composition_sector_number || null,
        composition_label: user.composition_label || "",
        roles: (user.roles || []).filter(r => r !== user.role),
        role_config: user.role_config || {}
      });
    } else {
      setEditingUser(null);
      setForm({
        username: "", password: "", full_name: "", title: "", email: "", phone: "",
        role: "user", position: "secretary", court_types: ["Μονομελές", "Πολυμελές"],
        court_branch: "Πρωτοδικείο Αθηνών", privacy_mode: false,
        allowed_pages: null, sector_numbers: []
      });
    }
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!form.username || !form.full_name) { toast.error("Συμπληρώστε τα υποχρεωτικά πεδία"); return; }
    if (!editingUser && !form.password) { toast.error("Ο κωδικός είναι υποχρεωτικός"); return; }
    const roleCt = ROLE_COURT_TYPE[form.role];
    if (roleCt) {
      if (form.role === "composition_president" && !(form.composition_label || "").trim()) { toast.error("Επιλέξτε Τομέα → Σύνθεση για τον Πρόεδρο Σύνθεσης"); return; }
      if (form.role !== "composition_president" && !(form.sector_numbers || []).length) { toast.error("Επιλέξτε τουλάχιστον έναν Τομέα"); return; }
    } else if (form.court_types.length === 0) { toast.error("Επιλέξτε τύπο δικαστηρίου"); return; }
    const payload = {
      ...form,
      court_types: roleCt ? [roleCt] : form.court_types,
      sector_numbers: roleCt && form.role !== "composition_president" ? form.sector_numbers : (form.sector_numbers && form.sector_numbers.length ? form.sector_numbers : (roleCt ? [] : null)),
      composition_sector_number: form.role === "composition_president" ? form.composition_sector_number : null,
      composition_label: form.role === "composition_president" ? form.composition_label : "",
      roles: [form.role, ...(form.roles || []).filter(r => r !== form.role)],
      role_config: Object.fromEntries(Object.entries(form.role_config || {}).filter(([k]) => (form.roles || []).includes(k) && k !== form.role)),
    };
    for (const r of (form.roles || [])) {
      if (r === form.role || !ROLE_COURT_TYPE[r]) continue;
      const cfg = payload.role_config[r] || {};
      if (r === "composition_president" && !(cfg.composition_label || "").trim()) { toast.error("Επιπλέον ιδιότητα «Πρόεδρος Σύνθεσης»: επιλέξτε Τομέα → Σύνθεση"); return; }
      if (r !== "composition_president" && !(cfg.sector_numbers || []).length) { toast.error(`Επιπλέον ιδιότητα: επιλέξτε Τομέα (${ROLE_COURT_TYPE[r]})`); return; }
    }

    try {
      setSaving(true);
      if (editingUser) {
        const updateData = {
          full_name: form.full_name, title: form.title || null, role: form.role, position: form.position,
          court_types: payload.court_types, email: form.email || null, phone: form.phone || null,
          privacy_mode: form.privacy_mode, allowed_pages: form.allowed_pages,
          sector_numbers: payload.sector_numbers, composition_sector_number: payload.composition_sector_number, composition_label: payload.composition_label,
          roles: payload.roles, role_config: payload.role_config
        };
        if (form.password) updateData.password = form.password;
        await axios.put(`${API}/users/${editingUser.id}`, updateData);
        toast.success("Ο χρήστης ενημερώθηκε");
      } else {
        await axios.post(`${API}/users`, payload);
        toast.success("Ο χρήστης δημιουργήθηκε");
      }
      setShowDialog(false);
      fetchUsers();
    } catch (error) { toast.error(error.response?.data?.detail || "Σφάλμα αποθήκευσης"); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await axios.delete(`${API}/users/${deleteId}`);
      toast.success("Ο χρήστης διαγράφηκε");
      fetchUsers();
    } catch { toast.error("Σφάλμα διαγραφής"); }
    finally { setDeleteId(null); }
  };

  return (
    <>
      <div className="mb-4"><PendingRegistrationsCard onChanged={fetchUsers} /></div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-slate-500">{users.length} χρήστες</p>
        <Button onClick={() => handleOpenDialog()} data-testid="add-user-btn">
          <Plus className="w-4 h-4 mr-2" />Νέος Χρήστης
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-4">{[1,2,3].map(i => <Skeleton key={i} className="h-16 w-full" />)}</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="font-semibold">Χρήστης</TableHead>
                  <TableHead className="font-semibold">Ιδιότητα</TableHead>
                  <TableHead className="font-semibold">Επικοινωνία</TableHead>
                  <TableHead className="font-semibold">Τύποι</TableHead>
                  <TableHead className="font-semibold">Κατάστημα</TableHead>
                  <TableHead className="font-semibold">Ρόλος</TableHead>
                  <TableHead className="font-semibold text-right">Ενέργειες</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((u) => (
                  <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          u.role === "admin" ? "bg-primary" : u.position === "judge" ? "bg-amber-500" :
                          u.position === "council_president" ? "bg-purple-500" : "bg-slate-200"
                        }`}>
                          {u.role === "admin" ? <Shield className="w-5 h-5 text-white" /> :
                           ["judge","council_president","council_member"].includes(u.position) ? 
                           <Gavel className="w-5 h-5 text-white" /> : <User className="w-5 h-5 text-slate-600" />}
                        </div>
                        <div>
                          <span className="font-medium text-slate-900">{u.full_name}</span>
                          <p className="text-xs text-slate-500">{u.username}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-slate-600">{positionLabels[u.position] || "Γραμματέας"}</TableCell>
                    <TableCell>
                      <div className="text-xs space-y-0.5">
                        {u.email && <div className="flex items-center gap-1 text-slate-600"><Mail className="w-3 h-3" />{u.email}</div>}
                        {u.phone && <div className="flex items-center gap-1 text-slate-600"><Phone className="w-3 h-3" />{u.phone}</div>}
                        {!u.email && !u.phone && <span className="text-slate-400">-</span>}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {(u.court_types || []).map(ct => (
                          <Badge key={ct} className={ct === "Μονομελές" ? "court-mono" : "court-poly"}>{ct.charAt(0)}</Badge>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="text-xs text-slate-600">{u.court_branch || "Πρωτοδικείο Αθηνών"}</span>
                    </TableCell>
                    <TableCell>
                      <Badge className={
                        u.role === "admin" ? "bg-primary" : 
                        u.role === "katastima" ? "bg-emerald-600 text-white" : 
                        u.role === "dispatch" ? "bg-orange-500 text-white" : 
                        u.role === "judge" ? "bg-amber-500 text-white" : 
                        ROLE_COURT_TYPE[u.role] ? "bg-violet-600 text-white" :
                        "bg-slate-200 text-slate-700"
                      } data-testid={`user-role-badge-${u.id}`}>
                        {ROLE_BADGE[u.role] || "Χρήστης"}
                      </Badge>
                      {ROLE_COURT_TYPE[u.role] && (
                        <p className="text-[10px] text-slate-500 mt-0.5" data-testid={`user-scope-${u.id}`}>
                          {u.role === "composition_president" ? (u.composition_label ? `«${u.composition_label}»${u.composition_sector_number ? ` · ${u.composition_sector_number}ος` : ""}` : "χωρίς Σύνθεση") : ((u.sector_numbers || []).length ? `Τομείς: ${u.sector_numbers.join(", ")}` : "χωρίς Τομείς")}
                        </p>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button variant="ghost" size="icon" onClick={() => handleOpenDialog(u)} data-testid={`edit-user-${u.id}`}><Edit className="w-4 h-4" /></Button>
                        <Button variant="ghost" size="icon" className="text-red-500 hover:text-red-600 hover:bg-red-50"
                          onClick={() => setDeleteId(u.id)} disabled={u.username === "admin"} data-testid={`delete-user-${u.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* User Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingUser ? "Επεξεργασία Χρήστη" : "Νέος Χρήστης"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
            <div className="space-y-2">
              <Label>Ονοματεπώνυμο *</Label>
              <Input value={form.full_name} onChange={e => setForm({...form, full_name: e.target.value})} data-testid="full-name-input" />
            </div>
            <div className="space-y-2">
              <Label>Τίτλος / Βαθμός <span className="text-xs text-slate-400">(εμφανίζεται σε υπογραφές αναφορών)</span></Label>
              <Input
                value={form.title}
                onChange={e => setForm({...form, title: e.target.value})}
                placeholder="π.χ. Πρόεδρος Πρωτοδικών, Υπεύθυνος Συντονισμού"
                data-testid="title-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Username *</Label>
              <Input value={form.username} onChange={e => setForm({...form, username: e.target.value})} disabled={!!editingUser} data-testid="username-input" />
            </div>
            <div className="space-y-2">
              <Label>Κωδικός {editingUser ? "(κενό = χωρίς αλλαγή)" : "*"}</Label>
              <Input type="password" value={form.password} onChange={e => setForm({...form, password: e.target.value})} data-testid="password-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Email</Label>
                <Input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} placeholder="user@example.com" data-testid="email-input" />
              </div>
              <div className="space-y-2">
                <Label>Τηλέφωνο</Label>
                <Input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} placeholder="+30697..." data-testid="phone-input" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Ιδιότητα</Label>
              <Select value={form.position} onValueChange={v => setForm({...form, position: v})}>
                <SelectTrigger data-testid="position-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="secretary">Δικαστικός/ή Γραμματέας</SelectItem>
                  <SelectItem value="department_head">Προϊστάμενος/η Τμήματος</SelectItem>
                  <SelectItem value="judge">Δικαστής</SelectItem>
                  <SelectItem value="council_president">Πρόεδρος Τριμελούς Συμβουλίου</SelectItem>
                  <SelectItem value="council_member">Μέλος Τριμελούς Συμβουλίου</SelectItem>
                  <SelectItem value="pretrial_secretariat_monomeles">Γραμματεία Προδικαστικού Μονομελούς</SelectItem>
                  <SelectItem value="pretrial_secretariat_polymeles">Γραμματεία Προδικαστικού Πολυμελούς</SelectItem>
                  <SelectItem value="prelodge_secretariat_monomeles">Γραμματεία Προκατάθεσης Μονομελούς</SelectItem>
                  <SelectItem value="prelodge_secretariat_polymeles">Γραμματεία Προκατάθεσης Πολυμελούς</SelectItem>
                  <SelectItem value="coordinator">Υπεύθυνος Συντονισμού</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {ROLE_COURT_TYPE[form.role] ? (
              <p className="text-xs text-slate-600 p-3 bg-slate-50 rounded-lg" data-testid="role-court-type-auto">Τύπος Δικαστηρίου: <b>{ROLE_COURT_TYPE[form.role]}</b> (αυτόματα από τον ρόλο)</p>
            ) : (
            <div className="space-y-2">
              <Label>Τύποι Δικαστηρίου</Label>
              <div className="space-y-2 p-3 bg-slate-50 rounded-lg">
                <div className="flex items-center space-x-2">
                  <Checkbox id="mono" checked={form.court_types.includes("Μονομελές")}
                    onCheckedChange={checked => {
                      setForm({...form, court_types: checked ? [...form.court_types, "Μονομελές"] : form.court_types.filter(ct => ct !== "Μονομελές")});
                    }} data-testid="court-mono-checkbox" />
                  <label htmlFor="mono" className="text-sm">Μονομελές</label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox id="poly" checked={form.court_types.includes("Πολυμελές")}
                    onCheckedChange={checked => {
                      setForm({...form, court_types: checked ? [...form.court_types, "Πολυμελές"] : form.court_types.filter(ct => ct !== "Πολυμελές")});
                    }} data-testid="court-poly-checkbox" />
                  <label htmlFor="poly" className="text-sm">Πολυμελές</label>
                </div>
              </div>
            </div>
            )}
            {form.position === "department_head" && (
              <div className="space-y-2">
                <Label>Τομείς Εποπτείας (μόνο για Προϊσταμένους)</Label>
                <div className="space-y-2 p-3 bg-amber-50 rounded-lg border border-amber-200">
                  <p className="text-xs text-slate-600 mb-2">Επιλέξτε τους Τομείς για τους οποίους ο χρήστης θα λαμβάνει το ημερήσιο δελτίο. Αν δεν επιλεχθεί κανένας → όλοι.</p>
                  {[1, 2, 3, 4].map(n => (
                    <div key={n} className="flex items-center space-x-2">
                      <Checkbox
                        id={`sec-${n}`}
                        checked={(form.sector_numbers || []).includes(n)}
                        onCheckedChange={checked => {
                          const cur = form.sector_numbers || [];
                          setForm({
                            ...form,
                            sector_numbers: checked ? [...cur, n] : cur.filter(s => s !== n)
                          });
                        }}
                        data-testid={`sector-${n}-checkbox`}
                      />
                      <label htmlFor={`sec-${n}`} className="text-sm">Τομέας {n}ος</label>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="space-y-2">
              <Label>Ρόλος</Label>
              <Select value={form.role} onValueChange={v => setForm({...form, role: v})}>
                <SelectTrigger data-testid="role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">Χρήστης</SelectItem>
                  <SelectItem value="admin">Διαχειριστής</SelectItem>
                  <SelectItem value="katastima">Κατάστημα</SelectItem>
                  <SelectItem value="dispatch">Προσωπικό Διακίνησης</SelectItem>
                  <SelectItem value="judge">Δικαστής</SelectItem>
                  <SelectItem value="sector_head_poly" data-testid="role-option-sector-head-poly">Υπεύθυνος/η Προσδιορισμού Τομέα Πολυμελούς</SelectItem>
                  <SelectItem value="sector_head_mono" data-testid="role-option-sector-head-mono">Υπεύθυνος/η Τομέα Δικαίου / Πρόεδρος Προσδιορισμού Μονομελούς</SelectItem>
                  <SelectItem value="composition_president" data-testid="role-option-composition-president">Πρόεδρος Πολυμελούς Σύνθεσης</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <RoleScopeFields form={form} setForm={setForm} />
            <ExtraRolesFields form={form} setForm={setForm} />
            <div className="space-y-2">
              <Label>Κατάστημα</Label>
              <Select
                value={form.court_branch}
                onValueChange={(v) => setForm({...form, court_branch: v})}
              >
                <SelectTrigger data-testid="court-branch-select">
                  <SelectValue placeholder="Επιλέξτε κατάστημα..." />
                </SelectTrigger>
                <SelectContent>
                  {availableBranches.map(b => (
                    <SelectItem key={b} value={b}>{b}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Απόρρητο</Label>
              <div className="flex items-center space-x-2 p-3 bg-slate-50 rounded-lg">
                <Checkbox id="privacy_mode" checked={form.privacy_mode}
                  onCheckedChange={checked => setForm({...form, privacy_mode: !!checked})}
                  data-testid="privacy-mode-checkbox" />
                <label htmlFor="privacy_mode" className="text-sm">
                  Ενεργοποίηση απορρήτου (ανωνυμοποίηση διαδίκων — εμφάνιση μόνο 2 πρώτων γραμμάτων)
                </label>
              </div>
            </div>
            {/* Page Access Control */}
            {form.role !== "admin" && (
              <div className="space-y-2">
                <Label>Πρόσβαση Σελίδων</Label>
                <div className="p-3 bg-slate-50 rounded-lg space-y-1">
                  <div className="flex items-center space-x-2 mb-2 pb-2 border-b">
                    <Checkbox id="all_pages" checked={!form.allowed_pages}
                      onCheckedChange={checked => setForm({...form, allowed_pages: checked ? null : ALL_PAGES.map(p => p.id)})}
                      data-testid="all-pages-checkbox" />
                    <label htmlFor="all_pages" className="text-sm font-medium">Όλες οι σελίδες</label>
                  </div>
                  {form.allowed_pages !== null && (
                    <div className="grid grid-cols-2 gap-1">
                      {ALL_PAGES.map(page => (
                        <div key={page.id} className="flex items-center space-x-2">
                          <Checkbox id={`page-${page.id}`}
                            checked={(form.allowed_pages || []).includes(page.id)}
                            onCheckedChange={checked => {
                              const current = form.allowed_pages || [];
                              setForm({...form, allowed_pages: checked ? [...current, page.id] : current.filter(p => p !== page.id)});
                            }}
                            data-testid={`page-${page.id}-checkbox`} />
                          <label htmlFor={`page-${page.id}`} className="text-xs">{page.label}</label>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Ακύρωση</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-user-btn">
              {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Αποθήκευση...</> : editingUser ? "Ενημέρωση" : "Δημιουργία"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Διαγραφή Χρήστη</DialogTitle>
            <DialogDescription>Η ενέργεια δεν μπορεί να αναιρεθεί.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteId(null)}>Ακύρωση</Button>
            <Button variant="destructive" onClick={handleDelete} data-testid="confirm-delete-user-btn">Διαγραφή</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

// ========== SMTP TAB ==========
const SmtpTab = () => {
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ court_type: "Μονομελές", smtp_host: "mail.protodikeio-ath.gr", smtp_port: 587, smtp_user: "", smtp_password: "", sender_email: "", sender_name: "Γραμματεία Δικαστηρίου", use_tls: true, webmail_url: "http://mail.protodikeio-ath.gr/" });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [customLabel, setCustomLabel] = useState("");

  useEffect(() => { fetchConfigs(); }, []);

  const fetchConfigs = async () => {
    try { setLoading(true); const r = await axios.get(`${API}/settings/smtp`); setConfigs(r.data); }
    catch { toast.error("Σφάλμα φόρτωσης"); }
    finally { setLoading(false); }
  };

  const handleSave = async () => {
    if (!form.smtp_host || !form.smtp_user || !form.smtp_password || !form.sender_email) {
      toast.error("Συμπληρώστε όλα τα πεδία"); return;
    }
    try {
      setSaving(true);
      await axios.post(`${API}/settings/smtp`, form);
      toast.success("Ρυθμίσεις SMTP αποθηκεύτηκαν");
      fetchConfigs();
    } catch (e) { toast.error(e.response?.data?.detail || "Σφάλμα"); }
    finally { setSaving(false); }
  };

  const handleTest = async () => {
    try {
      setTesting(true);
      const r = await axios.post(`${API}/settings/smtp/test`, form);
      r.data.success ? toast.success(r.data.message) : toast.error(r.data.message);
    } catch { toast.error("Σφάλμα δοκιμής"); }
    finally { setTesting(false); }
  };

  const handleDelete = async (courtType) => {
    if (!window.confirm(`Διαγραφή SMTP ρυθμίσεων για "${courtType}";`)) return;
    try {
      await axios.delete(`${API}/settings/smtp/${encodeURIComponent(courtType)}`);
      toast.success("Οι ρυθμίσεις διαγράφηκαν");
      fetchConfigs();
    } catch { toast.error("Σφάλμα διαγραφής"); }
  };

  const loadConfig = (courtType) => {
    const existing = configs.find(c => c.court_type === courtType);
    if (existing) {
      setForm({ ...existing, smtp_password: existing.smtp_password || "" });
    } else {
      setForm({ ...form, court_type: courtType, smtp_host: "mail.protodikeio-ath.gr", smtp_port: 587, smtp_user: "", smtp_password: "", sender_email: "", sender_name: "Γραμματεία Δικαστηρίου", use_tls: true, webmail_url: "http://mail.protodikeio-ath.gr/" });
    }
  };

  const applyPreset = (preset) => {
    if (preset === "cosmote") {
      setForm({ ...form, smtp_host: "mail.protodikeio-ath.gr", smtp_port: 587, use_tls: true, webmail_url: "http://mail.protodikeio-ath.gr/" });
    } else if (preset === "gmail") {
      setForm({ ...form, smtp_host: "smtp.gmail.com", smtp_port: 587, use_tls: true });
    }
  };

  const allLabels = [...new Set([...configs.map(c => c.court_type), "Μονομελές", "Πολυμελές"])];

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2"><Mail className="w-4 h-4" />Ρυθμίσεις SMTP</CardTitle>
          <CardDescription>Προσθέστε πολλαπλές ρυθμίσεις SMTP (Cosmote, Gmail, κλπ)</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Ετικέτα SMTP (τύπος/λογαριασμός)</Label>
              <div className="flex gap-2">
                <Select value={form.court_type} onValueChange={v => { setForm({...form, court_type: v}); loadConfig(v); }}>
                  <SelectTrigger data-testid="smtp-court-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {allLabels.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}
                    {customLabel && !allLabels.includes(customLabel) && <SelectItem value={customLabel}>{customLabel}</SelectItem>}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-2 mt-2">
                <Input placeholder="Νέα ετικέτα (π.χ. Gmail)" value={customLabel}
                  onChange={e => setCustomLabel(e.target.value)} className="flex-1" data-testid="custom-smtp-label" />
                <Button variant="outline" size="sm" disabled={!customLabel.trim()} onClick={() => {
                  if (customLabel.trim()) {
                    setForm({...form, court_type: customLabel.trim(), smtp_host: "", smtp_port: 587, smtp_user: "", smtp_password: "", sender_email: "", sender_name: "Γραμματεία", use_tls: true});
                    setCustomLabel("");
                  }
                }} data-testid="add-smtp-label-btn"><Plus className="w-4 h-4" /></Button>
              </div>
            </div>
            <div>
              <Label>Προρυθμίσεις</Label>
              <div className="flex gap-2 mt-1">
                <Button variant="outline" size="sm" onClick={() => applyPreset("cosmote")} data-testid="preset-cosmote">
                  Cosmote (mailgate.otenet.gr)
                </Button>
                <Button variant="outline" size="sm" onClick={() => applyPreset("gmail")} data-testid="preset-gmail">
                  Gmail (smtp.gmail.com)
                </Button>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>SMTP Host</Label>
              <Input value={form.smtp_host} onChange={e => setForm({...form, smtp_host: e.target.value})} placeholder="smtp.gmail.com" data-testid="smtp-host" />
            </div>
            <div className="space-y-2">
              <Label>SMTP Port</Label>
              <Input type="number" value={form.smtp_port} onChange={e => setForm({...form, smtp_port: parseInt(e.target.value) || 587})} data-testid="smtp-port" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Username</Label>
              <Input value={form.smtp_user} onChange={e => setForm({...form, smtp_user: e.target.value})} data-testid="smtp-user" />
            </div>
            <div className="space-y-2">
              <Label>Password</Label>
              <Input type="password" value={form.smtp_password} onChange={e => setForm({...form, smtp_password: e.target.value})} data-testid="smtp-password" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Email Αποστολέα</Label>
              <Input value={form.sender_email} onChange={e => setForm({...form, sender_email: e.target.value})} placeholder="court@example.com" data-testid="smtp-sender-email" />
            </div>
            <div className="space-y-2">
              <Label>Όνομα Αποστολέα</Label>
              <Input value={form.sender_name} onChange={e => setForm({...form, sender_name: e.target.value})} data-testid="smtp-sender-name" />
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox id="use_tls" checked={form.use_tls} onCheckedChange={checked => setForm({...form, use_tls: !!checked})} />
            <label htmlFor="use_tls" className="text-sm">Χρήση TLS</label>
          </div>
          <div className="space-y-2">
            <Label>Webmail URL (προαιρετικό — εμφανίζεται ως σύνδεσμος στο app)</Label>
            <Input
              value={form.webmail_url || ""}
              onChange={e => setForm({...form, webmail_url: e.target.value})}
              placeholder="http://mail.protodikeio-ath.gr/"
              data-testid="smtp-webmail-url"
            />
          </div>
          <div className="rounded border border-blue-200 bg-blue-50 p-3 text-xs text-blue-900">
            <b>Σημείωση:</b> Για να λειτουργήσει η αποστολή email μέσω του webmail του Πρωτοδικείου,
            ο διαχειριστής κάθε τύπου δικαστηρίου πρέπει να καταχωρήσει εδώ:
            <ul className="list-disc ml-5 mt-1">
              <li>SMTP Host: <code>mail.protodikeio-ath.gr</code></li>
              <li>SMTP Port: <code>587</code> (STARTTLS) ή <code>465</code> (SSL)</li>
              <li>Username (το email αποστολέα)</li>
              <li>Password</li>
            </ul>
            Αν δεν συμπληρωθούν, το σύστημα θα χρησιμοποιεί Resend API ως fallback.
          </div>
          <div className="flex gap-2">
            <Button onClick={handleSave} disabled={saving} data-testid="save-smtp-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}Αποθήκευση
            </Button>
            <Button variant="outline" onClick={handleTest} disabled={testing} data-testid="test-smtp-btn">
              {testing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}Δοκιμή Σύνδεσης
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* All Configured SMTPs */}
      {configs.length > 0 && (
        <Card>
          <CardHeader><CardTitle className="text-base">Ρυθμισμένα SMTP</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-2">
              {configs.map(cfg => (
                <div key={cfg.court_type} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg" data-testid={`smtp-config-${cfg.court_type}`}>
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-4 h-4 text-emerald-500" />
                    <div>
                      <span className="font-medium text-slate-900">{cfg.court_type}</span>
                      <p className="text-xs text-slate-500">
                        <b>Host:</b> {cfg.smtp_host}:{cfg.smtp_port} ({cfg.use_tls ? "TLS" : "no TLS"})
                      </p>
                      <p className="text-xs text-slate-500">
                        <b>User:</b> {cfg.smtp_user || "—"} · <b>Password:</b> {cfg.smtp_password ? "••••••••" : <span className="text-red-500">δεν έχει αποθηκευτεί</span>}
                      </p>
                      <p className="text-xs text-slate-500">
                        <b>Sender:</b> {cfg.sender_email}
                        {cfg.webmail_url && <> · <b>Webmail:</b> <a href={cfg.webmail_url} target="_blank" rel="noreferrer" className="text-blue-600 underline">link</a></>}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" onClick={() => { setForm({ ...cfg, smtp_password: cfg.smtp_password || "" }); }}
                      data-testid={`edit-smtp-${cfg.court_type}`}><Edit className="w-4 h-4" /></Button>
                    <Button variant="ghost" size="sm" className="text-red-500" onClick={() => handleDelete(cfg.court_type)}
                      data-testid={`delete-smtp-${cfg.court_type}`}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// ========== SMS TAB ==========
const SmsTab = () => {
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ court_type: "Μονομελές", twilio_account_sid: "", twilio_auth_token: "", twilio_phone_number: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchConfigs(); }, []);

  const fetchConfigs = async () => {
    try { setLoading(true); const r = await axios.get(`${API}/settings/sms`); setConfigs(r.data); }
    catch { toast.error("Σφάλμα φόρτωσης"); }
    finally { setLoading(false); }
  };

  const handleSave = async () => {
    if (!form.twilio_account_sid || !form.twilio_auth_token || !form.twilio_phone_number) {
      toast.error("Συμπληρώστε όλα τα πεδία"); return;
    }
    try {
      setSaving(true);
      await axios.post(`${API}/settings/sms`, form);
      toast.success("Ρυθμίσεις SMS αποθηκεύτηκαν");
      fetchConfigs();
    } catch (e) { toast.error(e.response?.data?.detail || "Σφάλμα"); }
    finally { setSaving(false); }
  };

  const loadConfig = (courtType) => {
    const existing = configs.find(c => c.court_type === courtType);
    if (existing) {
      setForm({ ...existing, twilio_auth_token: "" });
    } else {
      setForm({ court_type: courtType, twilio_account_sid: "", twilio_auth_token: "", twilio_phone_number: "" });
    }
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2"><MessageSquare className="w-4 h-4" />Ρυθμίσεις SMS (Twilio)</CardTitle>
          <CardDescription>Ρυθμίσεις Twilio ανά τύπο δικαστηρίου για αποστολή SMS</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Τύπος Δικαστηρίου</Label>
            <Select value={form.court_type} onValueChange={v => { setForm({...form, court_type: v}); loadConfig(v); }}>
              <SelectTrigger data-testid="sms-court-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Μονομελές">Μονομελές</SelectItem>
                <SelectItem value="Πολυμελές">Πολυμελές</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Account SID</Label>
            <Input value={form.twilio_account_sid} onChange={e => setForm({...form, twilio_account_sid: e.target.value})} placeholder="AC..." data-testid="twilio-sid" />
          </div>
          <div className="space-y-2">
            <Label>Auth Token</Label>
            <Input type="password" value={form.twilio_auth_token} onChange={e => setForm({...form, twilio_auth_token: e.target.value})} data-testid="twilio-token" />
          </div>
          <div className="space-y-2">
            <Label>Αριθμός Twilio (E.164)</Label>
            <Input value={form.twilio_phone_number} onChange={e => setForm({...form, twilio_phone_number: e.target.value})} placeholder="+30210..." data-testid="twilio-phone" />
          </div>
          <Button onClick={handleSave} disabled={saving} data-testid="save-sms-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}Αποθήκευση
          </Button>
        </CardContent>
      </Card>

      {configs.length > 0 && (
        <Card>
          <CardHeader><CardTitle className="text-base">Κατάσταση SMS</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-2">
              {["Μονομελές", "Πολυμελές"].map(ct => {
                const cfg = configs.find(c => c.court_type === ct);
                return (
                  <div key={ct} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <span className="font-medium">{ct}</span>
                    {cfg ? (
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-emerald-500" />
                        <span className="text-sm text-emerald-600">Ρυθμισμένο ({cfg.twilio_phone_number})</span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <XCircle className="w-4 h-4 text-slate-400" />
                        <span className="text-sm text-slate-400">Δεν έχει ρυθμιστεί</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// ========== TEMPLATES TAB ==========
const TemplatesTab = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchTemplates(); }, []);

  const fetchTemplates = async () => {
    try { setLoading(true); const r = await axios.get(`${API}/settings/templates`); setTemplates(r.data); }
    catch { toast.error("Σφάλμα φόρτωσης"); }
    finally { setLoading(false); }
  };

  const handleSave = async () => {
    if (!editingTemplate) return;
    try {
      setSaving(true);
      await axios.put(`${API}/settings/templates/${editingTemplate.template_type}`, editingTemplate);
      toast.success("Το template αποθηκεύτηκε");
      setEditingTemplate(null);
      fetchTemplates();
    } catch { toast.error("Σφάλμα αποθήκευσης"); }
    finally { setSaving(false); }
  };

  const typeLabels = {
    pretrial_assignment: "Email - Ορισμός Δικαστή Προδικαστικού",
    pretrial_assignment_sms: "SMS - Ορισμός Δικαστή Προδικαστικού"
  };

  return (
    <div className="space-y-4">
      {loading ? (
        <div className="space-y-4">{[1,2].map(i => <Skeleton key={i} className="h-24 w-full" />)}</div>
      ) : editingTemplate ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">{typeLabels[editingTemplate.template_type] || editingTemplate.template_type}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Θέμα</Label>
              <Input value={editingTemplate.subject} onChange={e => setEditingTemplate({...editingTemplate, subject: e.target.value})} data-testid="template-subject" />
            </div>
            <div className="space-y-2">
              <Label>Σώμα</Label>
              {/* Toolbar */}
              <div className="flex flex-wrap gap-1 p-2 border rounded-t-lg bg-slate-50">
                {[
                  { label: "B", tag: "<b>", end: "</b>", title: "Έντονα" },
                  { label: "I", tag: "<i>", end: "</i>", title: "Πλάγια", italic: true },
                  { label: "U", tag: "<u>", end: "</u>", title: "Υπογράμμιση" },
                ].map(btn => (
                  <Button key={btn.label} variant="outline" size="sm" className="h-7 w-7 p-0" title={btn.title}
                    style={btn.italic ? {fontStyle: "italic"} : btn.label === "B" ? {fontWeight: "bold"} : {textDecoration: "underline"}}
                    onClick={() => {
                      const ta = document.querySelector('[data-testid="template-body"]');
                      if (ta) {
                        const start = ta.selectionStart;
                        const end = ta.selectionEnd;
                        const text = editingTemplate.body;
                        const selected = text.substring(start, end);
                        const newText = text.substring(0, start) + btn.tag + selected + btn.end + text.substring(end);
                        setEditingTemplate({...editingTemplate, body: newText});
                      }
                    }}>{btn.label}</Button>
                ))}
                <div className="h-7 w-px bg-slate-300 mx-1" />
                {[
                  { label: "{judge_name}", title: "Ονοματεπώνυμο Δικαστή" },
                  { label: "{case_gak}", title: "Αριθμός ΓΑΚ" },
                  { label: "{case_year}", title: "Έτος ΓΑΚ" },
                  { label: "{assigned_date}", title: "Ημερομηνία Ανάθεσης" },
                  { label: "{court_type}", title: "Τύπος Δικαστηρίου" },
                  { label: "{plaintiffs}", title: "Ενάγοντες" },
                  { label: "{defendants}", title: "Εναγόμενοι" },
                  { label: "{hearing_date}", title: "Ημ. Δικασίμου" },
                ].map(ph => (
                  <Button key={ph.label} variant="ghost" size="sm" className="h-7 text-xs text-blue-600 px-2" title={ph.title}
                    onClick={() => setEditingTemplate({...editingTemplate, body: editingTemplate.body + ph.label})}
                    data-testid={`placeholder-${ph.label}`}>{ph.label}</Button>
                ))}
              </div>
              <Textarea rows={12} value={editingTemplate.body}
                onChange={e => setEditingTemplate({...editingTemplate, body: e.target.value})}
                className="rounded-t-none font-mono text-sm" data-testid="template-body" />
            </div>
            <div className="bg-blue-50 p-3 rounded-lg text-sm text-blue-700">
              <p className="font-medium mb-1">Placeholders: Κλικ στα κουμπιά πάνω ή πληκτρολογήστε:</p>
              <p className="text-xs font-mono">{"{judge_name} {case_gak} {case_year} {assigned_date} {court_type} {plaintiffs} {defendants} {hearing_date}"}</p>
            </div>
            {/* Preview — ασφαλές rendering: ο user input γίνεται escaped από τη React */}
            {editingTemplate.body && (
              <div className="space-y-1">
                <Label className="text-xs text-slate-500">Προεπισκόπηση:</Label>
                <div className="border rounded-lg p-4 bg-white text-sm whitespace-pre-wrap" data-testid="template-preview">
                  {(() => {
                    const PLACEHOLDERS = {
                      "{judge_name}": "[Όνομα Δικαστή]",
                      "{case_gak}": "[ΓΑΚ]",
                      "{case_year}": "[Έτος]",
                      "{assigned_date}": "[Ημ/νία]",
                      "{court_type}": "[Τύπος]",
                      "{plaintiffs}": "[Ενάγοντες]",
                      "{defendants}": "[Εναγόμενοι]",
                      "{hearing_date}": "[Δικάσιμος]",
                    };
                    const keys = Object.keys(PLACEHOLDERS);
                    // Split το body σε tokens (text + placeholders) ασφαλώς, χωρίς dangerouslySetInnerHTML.
                    const rx = new RegExp("(" + keys.map(k => k.replace(/[{}]/g, "\\$&")).join("|") + ")", "g");
                    const parts = (editingTemplate.body || "").split(rx);
                    return parts.map((p, i) => (
                      keys.includes(p)
                        ? <span key={i} className="text-blue-600 font-medium">{PLACEHOLDERS[p]}</span>
                        : <span key={i}>{p}</span>
                    ));
                  })()}
                </div>
              </div>
            )}
            <div className="flex gap-2">
              <Button onClick={handleSave} disabled={saving} data-testid="save-template-btn">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}Αποθήκευση
              </Button>
              <Button variant="outline" onClick={() => setEditingTemplate(null)}>Ακύρωση</Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        templates.map(t => (
          <Card key={t.template_type} className="cursor-pointer hover:ring-1 hover:ring-primary/30 transition-all" onClick={() => setEditingTemplate({...t})}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-slate-900">{typeLabels[t.template_type] || t.template_type}</p>
                  <p className="text-sm text-slate-500 mt-0.5">Θέμα: {t.subject}</p>
                </div>
                <Edit className="w-4 h-4 text-slate-400" />
              </div>
            </CardContent>
          </Card>
        ))
      )}
    </div>
  );
};

// ========== CASE DELETION TAB ==========
const CaseDeletionTab = () => {
  const [courtType, setCourtType] = useState("Μονομελές");
  const [caseCount, setCaseCount] = useState(null);
  const [loadingCount, setLoadingCount] = useState(false);
  const [deletingAll, setDeletingAll] = useState(false);
  const [deleteResult, setDeleteResult] = useState(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [singleGak, setSingleGak] = useState("");
  const [singleYear, setSingleYear] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [deletingCaseId, setDeletingCaseId] = useState(null);
  const [showSingleConfirm, setShowSingleConfirm] = useState(false);
  const [caseToDelete, setCaseToDelete] = useState(null);

  const fetchCount = async (ct) => {
    try {
      setLoadingCount(true);
      const r = await axios.get(`${API}/cases?court_type=${encodeURIComponent(ct)}&page=1&limit=1`);
      setCaseCount(r.data.total_count || 0);
    } catch { setCaseCount(null); }
    finally { setLoadingCount(false); }
  };

  useEffect(() => { fetchCount(courtType); }, [courtType]);

  const handleDeleteAllByCourtType = async () => {
    if (confirmText !== courtType) {
      toast.error(`Πληκτρολογήστε "${courtType}" για επιβεβαίωση`);
      return;
    }
    try {
      setDeletingAll(true);
      const r = await axios.post(`${API}/admin/cases/delete-by-court-type`, { court_type: courtType });
      setDeleteResult(r.data.message);
      toast.success(r.data.message);
      setShowConfirmDialog(false);
      setConfirmText("");
      fetchCount(courtType);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Σφάλμα διαγραφής");
    } finally { setDeletingAll(false); }
  };

  const handleSearchCase = async () => {
    if (!singleGak) { toast.error("Εισάγετε αριθμό ΓΑΚ"); return; }
    try {
      setSearching(true);
      const params = new URLSearchParams({ search: singleGak, page: "1", limit: "20" });
      if (singleYear) params.append("year", singleYear);
      const r = await axios.get(`${API}/cases?${params.toString()}`);
      setSearchResults(r.data.cases || []);
      if ((r.data.cases || []).length === 0) toast.info("Δεν βρέθηκαν υποθέσεις");
    } catch { toast.error("Σφάλμα αναζήτησης"); }
    finally { setSearching(false); }
  };

  const handleDeleteSingleCase = async () => {
    if (!caseToDelete) return;
    try {
      setDeletingCaseId(caseToDelete.id);
      await axios.delete(`${API}/cases/${caseToDelete.id}`);
      toast.success(`Η υπόθεση ΓΑΚ ${caseToDelete.gak_number}/${caseToDelete.gak_year} διαγράφηκε`);
      setSearchResults(prev => prev.filter(c => c.id !== caseToDelete.id));
      setShowSingleConfirm(false);
      setCaseToDelete(null);
      fetchCount(courtType);
    } catch (e) { toast.error(e.response?.data?.detail || "Σφάλμα διαγραφής"); }
    finally { setDeletingCaseId(null); }
  };

  return (
    <div className="space-y-4">
      {/* Single case deletion */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Trash2 className="w-4 h-4 text-red-500" />
            Διαγραφή Μεμονωμένης Υπόθεσης
          </CardTitle>
          <CardDescription>Αναζητήστε υπόθεση με ΓΑΚ και διαγράψτε τη</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Αριθμός ΓΑΚ</Label>
              <Input value={singleGak} onChange={e => setSingleGak(e.target.value)}
                placeholder="π.χ. 12345" className="w-36" data-testid="delete-search-gak" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Έτος (προαιρ.)</Label>
              <Input value={singleYear} onChange={e => setSingleYear(e.target.value)}
                placeholder="π.χ. 2025" className="w-28" data-testid="delete-search-year" />
            </div>
            <Button onClick={handleSearchCase} disabled={searching} data-testid="delete-search-btn">
              {searching ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
              Αναζήτηση
            </Button>
          </div>

          {searchResults.length > 0 && (
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="font-semibold">ΓΑΚ</TableHead>
                    <TableHead className="font-semibold">Τύπος</TableHead>
                    <TableHead className="font-semibold">Ενάγοντες</TableHead>
                    <TableHead className="font-semibold">Εναγόμενοι</TableHead>
                    <TableHead className="font-semibold text-right">Ενέργεια</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {searchResults.map(c => (
                    <TableRow key={c.id} data-testid={`delete-case-row-${c.id}`}>
                      <TableCell className="font-mono font-semibold">{c.gak_number}/{c.gak_year}</TableCell>
                      <TableCell><Badge className={c.court_type === "Μονομελές" ? "court-mono" : "court-poly"}>{c.court_type}</Badge></TableCell>
                      <TableCell className="text-sm text-slate-600 truncate max-w-[200px]">{c.plaintiffs || "-"}</TableCell>
                      <TableCell className="text-sm text-slate-500 truncate max-w-[200px]">{c.defendants || "-"}</TableCell>
                      <TableCell className="text-right">
                        <Button variant="destructive" size="sm"
                          onClick={() => { setCaseToDelete(c); setShowSingleConfirm(true); }}
                          disabled={deletingCaseId === c.id}
                          data-testid={`delete-single-case-${c.id}`}>
                          {deletingCaseId === c.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Bulk deletion by court type */}
      <Card className="border-red-200">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2 text-red-700">
            <AlertTriangle className="w-4 h-4" />
            Μαζική Διαγραφή ανά Τύπο Δικαστηρίου
          </CardTitle>
          <CardDescription>Διαγράφει ΟΛΕΣ τις υποθέσεις του επιλεγμένου τύπου δικαστηρίου. Αυτή η ενέργεια δεν μπορεί να αναιρεθεί.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-end gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Τύπος Δικαστηρίου</Label>
              <Select value={courtType} onValueChange={setCourtType}>
                <SelectTrigger className="w-48" data-testid="bulk-delete-court-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Μονομελές">Μονομελές</SelectItem>
                  <SelectItem value="Πολυμελές">Πολυμελές</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              {loadingCount ? (
                <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
              ) : caseCount !== null ? (
                <Badge variant="outline" className="h-9 px-3 text-sm" data-testid="bulk-delete-count">
                  {caseCount} υποθέσεις
                </Badge>
              ) : null}
            </div>
          </div>
          <Button variant="destructive" onClick={() => setShowConfirmDialog(true)}
            disabled={!caseCount || caseCount === 0}
            data-testid="bulk-delete-btn">
            <Trash2 className="w-4 h-4 mr-2" />
            Διαγραφή Όλων ({courtType})
          </Button>
          {deleteResult && <p className="text-sm text-emerald-700 font-medium">{deleteResult}</p>}
        </CardContent>
      </Card>

      {/* Confirm Bulk Delete Dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              Επιβεβαίωση Μαζικής Διαγραφής
            </DialogTitle>
            <DialogDescription>
              Πρόκειται να διαγράψετε <strong>{caseCount}</strong> υποθέσεις τύπου <strong>{courtType}</strong>.
              Αυτή η ενέργεια δεν μπορεί να αναιρεθεί!
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-3">
            <Label>Πληκτρολογήστε "<strong>{courtType}</strong>" για επιβεβαίωση:</Label>
            <Input value={confirmText} onChange={e => setConfirmText(e.target.value)}
              placeholder={courtType} data-testid="bulk-delete-confirm-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowConfirmDialog(false); setConfirmText(""); }}>Ακύρωση</Button>
            <Button variant="destructive" onClick={handleDeleteAllByCourtType}
              disabled={confirmText !== courtType || deletingAll}
              data-testid="bulk-delete-confirm-btn">
              {deletingAll ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Διαγραφή...</> : "Οριστική Διαγραφή"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm Single Delete Dialog */}
      <Dialog open={showSingleConfirm} onOpenChange={setShowSingleConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Διαγραφή Υπόθεσης</DialogTitle>
            <DialogDescription>
              Θέλετε να διαγράψετε την υπόθεση <strong>ΓΑΚ {caseToDelete?.gak_number}/{caseToDelete?.gak_year} · ΕΑΚ {caseToDelete?.eak_number || "—"}/{caseToDelete?.gak_year}</strong>;
              Η ενέργεια δεν μπορεί να αναιρεθεί.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSingleConfirm(false)}>Ακύρωση</Button>
            <Button variant="destructive" onClick={handleDeleteSingleCase} data-testid="confirm-delete-single-case-btn">
              Διαγραφή
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ========== MAIN ADMIN PANEL ==========
const AdminPanel = () => {
  const ToolsTab = () => {
    const [recalculating, setRecalculating] = useState(false);
    const [recalcResult, setRecalcResult] = useState(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [previewData, setPreviewData] = useState(null);

    const handlePreviewRecalc = async () => {
      try {
        setPreviewLoading(true);
        setPreviewData(null);
        const r = await axios.get(`${API}/admin/recalculate-deadlines-preview?sample_size=10`);
        setPreviewData(r.data);
        const changed = r.data?.samples_changed || 0;
        toast.info(`Preview: ${changed}/${r.data?.samples_checked} sample υποθέσεις θα αλλάξουν προθεσμία`);
      } catch (e) {
        toast.error(e.response?.data?.detail || "Σφάλμα preview");
      } finally {
        setPreviewLoading(false);
      }
    };
    const [deletingDuplicates, setDeletingDuplicates] = useState(false);
    const [dupResult, setDupResult] = useState(null);
    const [cleaningInvalid, setCleaningInvalid] = useState(false);
    const [cleanResult, setCleanResult] = useState(null);
    const [fixingDates, setFixingDates] = useState(false);
    const [fixDatesResult, setFixDatesResult] = useState(null);
    const [completingOverdue, setCompletingOverdue] = useState(false);
    const [overdueResult, setOverdueResult] = useState(null);
    const [completingToday, setCompletingToday] = useState(false);
    const [todayResult, setTodayResult] = useState(null);
    // user (Νοέ 2026): φίλτρα Ολοκλήρωσης Εκπρόθεσμων/Σημερινών — Κατάστημα / Τμήμα / Δικαστικό έτος (15/9→14/9)
    const [ocBranch, setOcBranch] = useState("");
    const [ocCourtType, setOcCourtType] = useState("");
    const [ocJy, setOcJy] = useState("");
    const [jyOptions, setJyOptions] = useState([]);
    useEffect(() => {
      axios.get(`${API}/judicial-years`).then(r => {
        const cur = r.data?.current || "";
        const ys = new Set([...(r.data?.years || []), cur]);
        const m = /^(\d{4})-(\d{4})$/.exec(cur);
        if (m) { ys.add(`${+m[1] - 1}-${+m[2] - 1}`); ys.add(`${+m[1] - 2}-${+m[2] - 2}`); }
        setJyOptions([...ys].filter(Boolean).sort().reverse());
      }).catch(() => {});
    }, []);
    const ocParams = () => {
      const p = new URLSearchParams();
      if (ocBranch) p.set("branch", ocBranch);
      if (ocCourtType) p.set("court_type", ocCourtType);
      if (ocJy) p.set("judicial_year", ocJy);
      const q = p.toString();
      return q ? `?${q}` : "";
    };
    const ocScopeLabel = () => [ocBranch || "Όλα τα καταστήματα", ocCourtType || "Όλα τα τμήματα", ocJy ? `Δικ. έτος ${ocJy}` : "Όλα τα έτη"].join(" · ");
    const [bulkDeleting, setBulkDeleting] = useState(false);
    const [bulkDeleteResult, setBulkDeleteResult] = useState(null);
    const [repairResult, setRepairResult] = useState(null);
    const [eakRepairResult, setEakRepairResult] = useState(null);
    const [eakRepairFile, setEakRepairFile] = useState(null);
    const [eakRepairing, setEakRepairing] = useState(false);
    const [eakCleanResult, setEakCleanResult] = useState(null);
    const [eakCleaning, setEakCleaning] = useState(false);

    const handleCompleteToday = async () => {
      if (!window.confirm(`Επιβεβαίωση: Ολοκλήρωση ΣΗΜΕΡΙΝΩΝ προθεσμιών;\n${ocScopeLabel()}`)) return;
      try {
        setCompletingToday(true);
        setTodayResult("Σε εξέλιξη…");
        const r = await axios.post(`${API}/admin/complete-today${ocParams()}`);
        const d = r.data || {};
        const msg = `Ολοκληρώθηκαν ${d.deadlines_updated || 0} σημερινές προθεσμίες σε ${d.cases_updated || 0} υποθέσεις (${d.today || ""})`;
        setTodayResult(msg);
        toast.success(msg);
      } catch (e) {
        const msg = e.response?.data?.detail || e.message;
        setTodayResult("Σφάλμα: " + msg);
        toast.error(msg);
      } finally {
        setCompletingToday(false);
      }
    };

    const handleBulkDeleteAssignments = async (courtType) => {
      const confirmText = `ΠΡΟΣΟΧΗ! Θα διαγραφούν ΟΡΙΣΤΙΚΑ όλοι οι ορισμοί δικαστών για ${courtType}.\n\nΠληκτρολογήστε ${courtType} για επιβεβαίωση:`;
      const input = window.prompt(confirmText);
      if (input !== courtType) { toast.info("Ακυρώθηκε"); return; }
      try {
        setBulkDeleting(true);
        setBulkDeleteResult(`Εκτέλεση για ${courtType}…`);
        const r = await axios.delete(`${API}/admin/bulk-delete-judge-assignments?court_type=${encodeURIComponent(courtType)}`);
        const d = r.data || {};
        const msg = `${courtType}: Επηρεάστηκαν ${d.affected_cases || 0} υποθέσεις (cleared: ${d.cleared_modified_count || 0})`;
        setBulkDeleteResult(msg);
        toast.success(msg);
      } catch (e) {
        const msg = e.response?.data?.detail || e.message;
        setBulkDeleteResult("Σφάλμα: " + msg);
        toast.error(msg);
      } finally {
        setBulkDeleting(false);
      }
    };
    const [reminderDays, setReminderDays] = useState(7);
    const [sendingReminders, setSendingReminders] = useState(false);
    const [reminderResult, setReminderResult] = useState(null);
    
    const handleRecalculate = async () => {
      try {
        setRecalculating(true);
        setRecalcResult("");
        const r = await axios.post(`${API}/admin/recalculate-deadlines`);
        toast.info(r.data.message);
        setRecalcResult("Εκτελείται στο background...");
        
        // Poll for status
        const pollInterval = setInterval(async () => {
          try {
            const status = await axios.get(`${API}/admin/recalculate-status`);
            const d = status.data;
            if (d.status === "completed") {
              clearInterval(pollInterval);
              setRecalculating(false);
              const msg = `Ολοκληρώθηκε! Ενημερώθηκαν ${d.updated}, παραλείφθηκαν ${d.skipped}${d.errors > 0 ? `, σφάλματα ${d.errors}` : ""}`;
              setRecalcResult(msg);
              toast.success(msg);
            } else {
              setRecalcResult(`Σε εξέλιξη: ${d.updated || 0} ενημερωμένες, ${d.skipped || 0} παραλείφθηκαν (από ${d.total || "?"})`);
            }
          } catch { /* silent */ }
        }, 3000);
        
        // Safety: stop polling after 10 min
        setTimeout(() => {
          clearInterval(pollInterval);
          setRecalculating(false);
        }, 600000);
        
      } catch (e) {
        toast.error(e.response?.data?.detail || "Σφάλμα");
        setRecalculating(false);
      }
    };

    const handleDeleteDuplicates = async () => {
      try {
        setDeletingDuplicates(true);
        const r = await axios.post(`${API}/admin/delete-duplicates`);
        setDupResult(r.data.message);
        toast.success(r.data.message);
      } catch (e) {
        toast.error(e.response?.data?.detail || "Σφάλμα");
      } finally {
        setDeletingDuplicates(false);
      }
    };

    const handleCleanupInvalid = async () => {
      try {
        setCleaningInvalid(true);
        const r = await axios.post(`${API}/admin/cleanup-invalid`);
        setCleanResult(r.data.message);
        toast.success(r.data.message);
      } catch (e) {
        toast.error(e.response?.data?.detail || "Σφάλμα");
      } finally {
        setCleaningInvalid(false);
      }
    };

    const handleFixDates = async () => {
      try {
        setFixingDates(true);
        const r = await axios.post(`${API}/admin/fix-filing-dates`);
        setFixDatesResult(r.data.message);
        toast.success(r.data.message);
      } catch (e) {
        toast.error(e.response?.data?.detail || "Σφάλμα");
      } finally {
        setFixingDates(false);
      }
    };

    const handleCompleteOverdue = async () => {
      try {
        setCompletingOverdue(true);
        setOverdueResult("Ξεκίνησε στο background…");
        const r = await axios.post(`${API}/admin/complete-overdue${ocParams()}`);
        toast.info(r.data?.message || "Η εργασία ξεκίνησε στο background");
        // Poll status every 2s — όπως στον επαναυπολογισμό προθεσμιών
        const start = Date.now();
        const poll = async () => {
          try {
            const s = await axios.get(`${API}/admin/complete-overdue-status`);
            const data = s.data || {};
            if (data.status === "running") {
              const total = data.total || 0;
              const cu = data.cases_updated || 0;
              const du = data.deadlines_updated || 0;
              setOverdueResult(`Σε εξέλιξη… ${cu} υποθέσεις / ${du} στάδια (από ${total})`);
              if (Date.now() - start < 30 * 60 * 1000) setTimeout(poll, 2000);
              else { setOverdueResult("Λήξη χρόνου polling. Δείτε /admin/complete-overdue-status."); setCompletingOverdue(false); }
            } else if (data.status === "completed") {
              const msg = `Ολοκληρώθηκαν ${data.deadlines_updated || 0} στάδια σε ${data.cases_updated || 0} υποθέσεις${data.errors ? ` (${data.errors} σφάλματα)` : ""}`;
              setOverdueResult(msg);
              toast.success(msg);
              setCompletingOverdue(false);
            } else {
              setOverdueResult(`Κατάσταση: ${data.status || "άγνωστη"}`);
              setCompletingOverdue(false);
            }
          } catch (err) {
            setOverdueResult("Σφάλμα polling: " + (err.response?.data?.detail || err.message));
            setCompletingOverdue(false);
          }
        };
        setTimeout(poll, 1500);
      } catch (e) {
        toast.error(e.response?.data?.detail || "Σφάλμα");
        setCompletingOverdue(false);
      }
    };

    const handleSendReminders = async () => {
      try {
        setSendingReminders(true);
        const r = await axios.post(`${API}/notifications/send-pretrial-reminders?days_before=${reminderDays}`);
        const msg = `Στάλθηκαν ${r.data.sent}/${r.data.total_judges} email${r.data.errors?.length > 0 ? ` (${r.data.errors.length} σφάλματα)` : ""}`;
        setReminderResult(msg);
        toast.success(msg);
      } catch (e) {
        toast.error(e.response?.data?.detail || "Σφάλμα αποστολής");
      } finally {
        setSendingReminders(false);
      }
    };
    
    return (
      <div className="space-y-4">
        <SolonBulkCard />
        <DocketSettingsCard />
        <DocLinksCard />
        <BranchAttributionCard />
        <EakFixCard />
        <CourtTypeFixCard />
        <PartyAnonCard />
        <LegalDocsCard />
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Εργαλεία Συστήματος</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Delete Duplicates */}
            <div className="p-4 rounded-lg border bg-red-50 border-red-200">
              <h3 className="font-medium text-red-900">Διαγραφή Διπλοεγγραφών</h3>
              <p className="text-sm text-red-700 mt-1">Εντοπίζει και διαγράφει διπλοεγγραφές (ίδιο ΓΑΚ+Έτος), κρατώντας τη νεότερη εγγραφή</p>
              <Button className="mt-3" variant="destructive" onClick={handleDeleteDuplicates} disabled={deletingDuplicates}
                data-testid="delete-duplicates-btn">
                {deletingDuplicates ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Διαγραφή...</> : <><Trash2 className="w-4 h-4 mr-2" />Διαγραφή Διπλοεγγραφών</>}
              </Button>
              {dupResult && <p className="text-sm text-emerald-700 mt-2 font-medium">{dupResult}</p>}
            </div>

            {/* Cleanup Invalid Years */}
            <div className="p-4 rounded-lg border bg-orange-50 border-orange-200">
              <h3 className="font-medium text-orange-900">Εκκαθάριση Μη Έγκυρων Ετών</h3>
              <p className="text-sm text-orange-700 mt-1">Διαγράφει υποθέσεις με έτος ΓΑΚ μεγαλύτερο του 2026 (λανθασμένες εγγραφές)</p>
              <Button className="mt-3 bg-orange-600 hover:bg-orange-700 text-white" onClick={handleCleanupInvalid} disabled={cleaningInvalid}
                data-testid="cleanup-invalid-btn">
                {cleaningInvalid ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Εκκαθάριση...</> : "Εκκαθάριση Μη Έγκυρων"}
              </Button>
              {cleanResult && <p className="text-sm text-emerald-700 mt-2 font-medium">{cleanResult}</p>}
            </div>

            {/* Fix Filing Dates */}
            <div className="p-4 rounded-lg border bg-purple-50 border-purple-200">
              <h3 className="font-medium text-purple-900">Διόρθωση Ημερομηνιών Κατάθεσης</h3>
              <p className="text-sm text-purple-700 mt-1">Αφαιρεί λάθος ημερομηνίες κατάθεσης (έτος δεν ταιριάζει με ΓΑΚ). Μετά χρειάζεται επανεισαγωγή Excel σε λειτουργία "Ενημέρωση"</p>
              <Button className="mt-3 bg-purple-600 hover:bg-purple-700 text-white" onClick={handleFixDates} disabled={fixingDates}
                data-testid="fix-filing-dates-btn">
                {fixingDates ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Διόρθωση...</> : "Διόρθωση Ημερομηνιών"}
              </Button>
              {fixDatesResult && <p className="text-sm text-emerald-700 mt-2 font-medium">{fixDatesResult}</p>}
            </div>

            {/* Recalculate Deadlines */}
            <div className="p-4 rounded-lg border bg-amber-50 border-amber-200" data-testid="recalc-deadlines-card">
              <h3 className="font-medium text-amber-900">Επανυπολογισμός Προθεσμιών</h3>
              <p className="text-sm text-amber-700 mt-1">
                Εφαρμόζει στις υπάρχουσες υποθέσεις τους <strong>νέους κανόνες (Ν.5282/2026)</strong>:
              </p>
              <ul className="text-xs text-amber-800 mt-2 ml-4 list-disc space-y-0.5">
                <li><strong>Επίδοση</strong>: 30 ημ. για ΟΛΟΥΣ (εσωτερικού/εξωτερικού/αγνώστου διαμονής)</li>
                <li><strong>Προτάσεις</strong>: 90 ημ. (εσωτερικού/αγνώστου διαμονής) / 120 ημ. (εξωτερικού) από λήξη επίδοσης</li>
                <li><strong>Προσθήκη-Αντίκρουση</strong>: 15 ημ. για ΟΛΟΥΣ μετά τη λήξη προτάσεων</li>
                <li><strong>Σ/Κ shift</strong>: αν λήξη πέφτει Σάββατο/Κυριακή → επόμενη εργάσιμη</li>
                <li><strong>Chain</strong>: Ορισμός Δικαστή +10 ημ. → Προδικαστικός Έλεγχος +30 ημ.</li>
                <li>Διατηρούνται: Αύγουστος (εξαίρεση), Δημόσιο (1/7-15/9), διαμεσολάβηση</li>
              </ul>
              <div className="flex flex-wrap gap-2 mt-3">
                <Button variant="outline" className="border-blue-400 text-blue-700 hover:bg-blue-50"
                        onClick={handlePreviewRecalc} disabled={previewLoading}
                        data-testid="recalc-preview-btn">
                  {previewLoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Προεπισκόπηση...</> : "Προεπισκόπηση Αλλαγών"}
                </Button>
                <Button onClick={handleRecalculate} disabled={recalculating}
                        className="bg-amber-600 hover:bg-amber-700 text-white"
                        data-testid="recalculate-btn">
                  {recalculating ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Υπολογισμός...</> : "Εκτέλεση Επανυπολογισμού"}
                </Button>
              </div>
              {recalcResult && <p className="text-sm text-emerald-700 mt-2">{recalcResult}</p>}
              {previewData && (
                <div className="mt-3 p-3 bg-white border border-amber-300 rounded text-xs space-y-2" data-testid="recalc-preview-result">
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <Badge variant="outline" className="border-blue-300 text-blue-700">
                      Σύνολο: {previewData.total_cases} υποθέσεις
                    </Badge>
                    <Badge variant="outline" className="border-orange-300 text-orange-700">
                      Δείγμα ελέγχου: {previewData.samples_checked}
                    </Badge>
                    <Badge variant="outline" className="border-emerald-300 text-emerald-700">
                      Θα επηρεαστούν: {previewData.samples_changed}/{previewData.samples_checked}
                    </Badge>
                  </div>
                  {previewData.samples?.length > 0 && (
                    <div className="overflow-x-auto">
                      <table className="w-full text-[11px] border-collapse">
                        <thead className="bg-slate-100">
                          <tr>
                            <th className="p-1.5 text-left border">ΓΑΚ</th>
                            <th className="p-1.5 text-left border">Κατάθεση</th>
                            <th className="p-1.5 text-left border">Διαμονή</th>
                            <th className="p-1.5 text-left border">Παλιό</th>
                            <th className="p-1.5 text-left border">Νέο</th>
                            <th className="p-1.5 text-center border">Αλλαγή</th>
                          </tr>
                        </thead>
                        <tbody>
                          {previewData.samples.map((s, idx) => (
                            <tr key={idx} className={s.changed ? "bg-amber-50" : ""}>
                              <td className="p-1.5 border font-mono">{s.gak}</td>
                              <td className="p-1.5 border">{s.filing_date}</td>
                              <td className="p-1.5 border">
                                {s.residence === "domestic" ? "Εσωτ." : s.residence === "abroad" ? "Εξωτ." : "Άγν."}
                              </td>
                              <td className="p-1.5 border font-mono text-slate-600">{s.old_proposals_date || "—"}</td>
                              <td className="p-1.5 border font-mono text-emerald-700 font-semibold">{s.new_proposals_date || "—"}</td>
                              <td className="p-1.5 border text-center">
                                {s.changed ? <span className="text-amber-700 font-bold">ΑΛΛΑΓΗ</span> : <span className="text-slate-400">—</span>}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  <p className="text-[10px] text-slate-500 italic">
                    Σημείωση: Πιθανές διαφορές αφορούν μόνο τις προτάσεις. Το chain (Προσθήκη/Ορισμός/Προδικαστικός) μεταβάλλεται αντίστοιχα.
                  </p>
                </div>
              )}
            </div>

            {/* Complete Overdue */}
            <div className="p-4 rounded-lg border bg-emerald-50 border-emerald-200">
              <h3 className="font-medium text-emerald-900">Ολοκλήρωση Εκπρόθεσμων (μέχρι χθες)</h3>
              <p className="text-sm text-emerald-700 mt-1">Σημειώνει ως "ολοκληρωμένα" τα στάδια με ημερομηνία &lt; σήμερα (όχι σημερινές προθεσμίες)</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mt-3" data-testid="overdue-filters">
                <div>
                  <Label className="text-xs text-emerald-900">Κατάστημα</Label>
                  <BranchFilterSelect value={ocBranch} onChange={setOcBranch} className="w-full bg-white" testId="overdue-branch-filter" />
                </div>
                <div>
                  <Label className="text-xs text-emerald-900">Τμήμα</Label>
                  <Select value={ocCourtType || "__all__"} onValueChange={(v) => setOcCourtType(v === "__all__" ? "" : v)}>
                    <SelectTrigger className="bg-white" data-testid="overdue-court-type-filter"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__all__">Τμήμα: Όλα</SelectItem>
                      <SelectItem value="Μονομελές">Μονομελές</SelectItem>
                      <SelectItem value="Πολυμελές">Πολυμελές</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-emerald-900">Δικαστικό έτος προθεσμιών (15/9→14/9)</Label>
                  <Select value={ocJy || "__all__"} onValueChange={(v) => setOcJy(v === "__all__" ? "" : v)}>
                    <SelectTrigger className="bg-white" data-testid="overdue-jy-filter"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__all__">Δικαστικό έτος: Όλα</SelectItem>
                      {jyOptions.map(y => <SelectItem key={y} value={y}>{y}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <p className="text-xs text-emerald-800 mt-2" data-testid="overdue-scope-label">Πεδίο εφαρμογής: {ocScopeLabel()}</p>
              <div className="flex flex-wrap gap-2 mt-3">
                <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={handleCompleteOverdue} disabled={completingOverdue}
                  data-testid="complete-overdue-btn">
                  {completingOverdue ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Ολοκλήρωση...</> : "Ολοκλήρωση Εκπρόθεσμων (μέχρι χθες)"}
                </Button>
                <Button variant="outline" className="border-orange-400 text-orange-700 hover:bg-orange-50" onClick={handleCompleteToday} disabled={completingToday}
                  data-testid="complete-today-btn">
                  {completingToday ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Ολοκλήρωση...</> : "Ολοκλήρωση Σημερινών"}
                </Button>
              </div>
              {overdueResult && <p className="text-sm text-emerald-700 mt-2 font-medium">{overdueResult}</p>}
              {todayResult && <p className="text-sm text-orange-700 mt-2 font-medium">{todayResult}</p>}
            </div>

            {/* Δικαστές Έδρας από Προδικαστικό */}
            <BenchJudgeSyncCard />
            <WorkloadLimitCard />
            <ReassignmentDigestCard />

            {/* Repair Last Hearing Date */}
            <div className="p-4 rounded-lg border bg-amber-50 border-amber-300">
              <h3 className="font-medium text-amber-900 flex items-center gap-2">
                <Wrench className="w-4 h-4" />Διόρθωση Πινακίων (last_hearing_date)
              </h3>
              <p className="text-sm text-amber-800 mt-1">
                ⚠ Εντοπίζει υποθέσεις των οποίων το πεδίο πινακίου έχει αλλοιωθεί σε σκέτη ημερομηνία ISO (πχ "2026-09-22"). 
                Συνήθης αιτία: re-import Excel όπου το cell ήταν datetime. 
                <b> Βήμα 1:</b> Έλεγχος (μόνο εμφάνιση). <b> Βήμα 2:</b> Διόρθωση (καθαρίζει corrupt εγγραφές). 
                <b> Βήμα 3:</b> Re-import του αρχικού Excel σου ώστε να επανέλθουν τα σωστά labels.
              </p>
              <div className="flex flex-wrap gap-2 mt-3">
                <Button variant="outline" className="border-amber-500 text-amber-800" onClick={async () => {
                  try {
                    const r = await axios.post(`${API}/admin/repair-last-hearing-date?dry_run=true`);
                    setRepairResult(r.data);
                    toast.info(r.data.message);
                  } catch (e) {
                    toast.error(e.response?.data?.detail || "Σφάλμα");
                  }
                }} data-testid="repair-lhd-check-btn">
                  <Search className="w-4 h-4 mr-2" />Βήμα 1: Έλεγχος (dry-run)
                </Button>
                <Button className="bg-amber-600 hover:bg-amber-700 text-white" onClick={async () => {
                  if (!window.confirm("ΠΡΟΣΟΧΗ: Θα καθαρίσει τα corrupt last_hearing_date των επηρεασμένων υποθέσεων (γίνονται null). Μετά πρέπει να κάνεις re-import του αρχικού Excel.\n\nΣυνέχεια;")) return;
                  try {
                    const r = await axios.post(`${API}/admin/repair-last-hearing-date?dry_run=false`);
                    setRepairResult(r.data);
                    toast.success(r.data.message);
                  } catch (e) {
                    toast.error(e.response?.data?.detail || "Σφάλμα");
                  }
                }} data-testid="repair-lhd-fix-btn">
                  <Wrench className="w-4 h-4 mr-2" />Βήμα 2: Διόρθωση
                </Button>
              </div>
              {repairResult && (
                <div className="mt-3 p-3 bg-white border border-amber-200 rounded text-xs">
                  <p className="font-semibold text-amber-900">Επηρεασμένες υποθέσεις: {repairResult.total_affected} · Διορθώθηκαν: {repairResult.fixed}</p>
                  {repairResult.samples && repairResult.samples.length > 0 && (
                    <div className="mt-2 max-h-40 overflow-auto">
                      <table className="w-full text-xs">
                        <thead><tr className="bg-amber-100"><th className="p-1 text-left">ΓΑΚ</th><th className="p-1 text-left">Δικαστήριο</th><th className="p-1 text-left">Αλλοιωμένο πεδίο</th></tr></thead>
                        <tbody>
                          {repairResult.samples.slice(0, 30).map((s, i) => (
                            <tr key={i} className="border-t"><td className="p-1">{s.gak}</td><td className="p-1">{s.court_type}</td><td className="p-1 font-mono text-red-600">{s.corrupt_lhd}</td></tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Repair EAK from Excel */}
            <div className="p-4 rounded-lg border bg-blue-50 border-blue-300">
              <h3 className="font-medium text-blue-900 flex items-center gap-2">
                <Wrench className="w-4 h-4" />Επιδιόρθωση ΕΑΚ Υποθέσεων (από Excel)
              </h3>
              <p className="text-sm text-blue-800 mt-1">
                Διόρθωση υποθέσεων όπου το πεδίο <b>Ε.Α.Κ.</b> (στήλη C του template) δεν εισήχθη σωστά κατά την αρχική εισαγωγή.
                Ανεβάστε ξανά το <b>ίδιο Excel</b> (ή ένα απλοποιημένο με 3 στήλες: A=ΓΑΚ Αριθμός, B=Έτος, C=Ε.Α.Κ.) και θα ενημερωθεί
                <b> μόνο</b> το πεδίο EAK για τις υπάρχουσες υποθέσεις (match by ΓΑΚ + Έτος). Καθαρίζει αυτόματα και το «.0» suffix.
                <br />
                <span className="text-blue-700">⚠ Δεν εισάγει νέες υποθέσεις, δεν τροποποιεί άλλα πεδία (πινάκιο, διάδικοι, ημερομηνίες).</span>
              </p>
              <div className="flex flex-wrap items-center gap-2 mt-3">
                <Input
                  type="file"
                  accept=".xlsx,.xls,.ods,.csv"
                  onChange={(e) => setEakRepairFile(e.target.files?.[0] || null)}
                  className="max-w-sm"
                  data-testid="repair-eak-file-input"
                />
                <Button
                  variant="outline"
                  className="border-blue-500 text-blue-800"
                  disabled={!eakRepairFile || eakRepairing}
                  onClick={async () => {
                    if (!eakRepairFile) return;
                    try {
                      setEakRepairing(true);
                      const fd = new FormData(); fd.append("file", eakRepairFile);
                      const r = await axios.post(`${API}/admin/repair-eak-from-excel?dry_run=true`, fd, { headers: { "Content-Type": "multipart/form-data" } });
                      setEakRepairResult(r.data);
                      toast.info(r.data.message);
                    } catch (e) {
                      toast.error(e.response?.data?.detail || "Σφάλμα");
                    } finally { setEakRepairing(false); }
                  }}
                  data-testid="repair-eak-dryrun-btn"
                >
                  <Search className="w-4 h-4 mr-2" />Βήμα 1: Έλεγχος (dry-run)
                </Button>
                <Button
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  disabled={!eakRepairFile || eakRepairing}
                  onClick={async () => {
                    if (!eakRepairFile) return;
                    if (!window.confirm("Θα ενημερωθούν τα EAK των υπαρχουσών υποθέσεων (match ΓΑΚ+Έτος). Συνέχεια;")) return;
                    try {
                      setEakRepairing(true);
                      const fd = new FormData(); fd.append("file", eakRepairFile);
                      const r = await axios.post(`${API}/admin/repair-eak-from-excel?dry_run=false`, fd, { headers: { "Content-Type": "multipart/form-data" } });
                      setEakRepairResult(r.data);
                      toast.success(r.data.message);
                    } catch (e) {
                      toast.error(e.response?.data?.detail || "Σφάλμα");
                    } finally { setEakRepairing(false); }
                  }}
                  data-testid="repair-eak-apply-btn"
                >
                  {eakRepairing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Wrench className="w-4 h-4 mr-2" />}
                  Βήμα 2: Εφαρμογή
                </Button>
              </div>
              {eakRepairResult && (
                <div className="mt-3 p-3 bg-white border border-blue-200 rounded text-xs">
                  <p className="font-semibold text-blue-900">
                    Σύνολο γραμμών: {eakRepairResult.total_rows} · Ενημερώθηκαν: {eakRepairResult.updated} ·
                    Δεν βρέθηκαν: {eakRepairResult.not_found} · Ήδη σωστά: {eakRepairResult.already_correct} ·
                    Χωρίς EAK: {eakRepairResult.skipped_no_eak}
                  </p>
                  {eakRepairResult.samples && eakRepairResult.samples.length > 0 && (
                    <div className="mt-2 max-h-40 overflow-auto">
                      <table className="w-full text-xs">
                        <thead><tr className="bg-blue-100"><th className="p-1 text-left">ΓΑΚ</th><th className="p-1 text-left">Παλιό EAK</th><th className="p-1 text-left">Νέο EAK</th></tr></thead>
                        <tbody>
                          {eakRepairResult.samples.slice(0, 30).map((s, i) => (
                            <tr key={i} className="border-t">
                              <td className="p-1 font-mono">{s.gak}</td>
                              <td className="p-1 font-mono text-red-600">{s.old_eak}</td>
                              <td className="p-1 font-mono text-emerald-700 font-semibold">{s.new_eak}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
              {/* Σύντομος καθαρισμός .0 suffix χωρίς upload */}
              <div className="mt-4 pt-3 border-t border-blue-200">
                <p className="text-sm text-blue-800 mb-2">
                  Επιπλέον, καθαρισμός υπαρχόντων EAK που έχουν αποθηκευτεί ως float (πχ <code className="bg-blue-100 px-1">500.0</code>) ή «nan» χωρίς upload:
                </p>
                <div className="flex flex-wrap gap-2">
                  <Button variant="outline" className="border-blue-500 text-blue-800" disabled={eakCleaning} onClick={async () => {
                    try {
                      setEakCleaning(true);
                      const r = await axios.post(`${API}/admin/clean-eak-suffix?dry_run=true`);
                      setEakCleanResult(r.data); toast.info(r.data.message);
                    } catch (e) { toast.error(e.response?.data?.detail || "Σφάλμα"); }
                    finally { setEakCleaning(false); }
                  }} data-testid="clean-eak-dryrun-btn">
                    <Search className="w-4 h-4 mr-2" />Έλεγχος (dry-run)
                  </Button>
                  <Button className="bg-blue-600 hover:bg-blue-700 text-white" disabled={eakCleaning} onClick={async () => {
                    if (!window.confirm("Θα καθαριστεί το .0 / nan suffix από υπάρχοντα EAK στη βάση. Συνέχεια;")) return;
                    try {
                      setEakCleaning(true);
                      const r = await axios.post(`${API}/admin/clean-eak-suffix?dry_run=false`);
                      setEakCleanResult(r.data); toast.success(r.data.message);
                    } catch (e) { toast.error(e.response?.data?.detail || "Σφάλμα"); }
                    finally { setEakCleaning(false); }
                  }} data-testid="clean-eak-apply-btn">
                    {eakCleaning ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Wrench className="w-4 h-4 mr-2" />}
                    Καθαρισμός
                  </Button>
                </div>
                {eakCleanResult && (
                  <div className="mt-2 p-2 bg-white border border-blue-200 rounded text-xs">
                    <p className="font-semibold text-blue-900">
                      Βρέθηκαν: {eakCleanResult.total_affected} · Καθαρίστηκαν: {eakCleanResult.fixed}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Bulk Delete Judge Assignments */}
            <div className="p-4 rounded-lg border bg-red-50 border-red-300">
              <h3 className="font-medium text-red-900 flex items-center gap-2"><Trash2 className="w-4 h-4" />ΑΜΕΣΗ ΟΡΙΣΤΙΚΗ Διαγραφή ΟΛΩΝ των Ορισμών Δικαστών</h3>
              <p className="text-sm text-red-700 mt-1">⚠ Καθαρίζει ΟΛΟΥΣ τους ορισμούς δικαστών &amp; Πράξεις Ορισμού ανά τύπο δικαστηρίου. Οι υποθέσεις επανέρχονται σε <b>προ-ορισμού</b> κατάσταση. Δεν αναιρείται.</p>
              <div className="flex flex-wrap gap-2 mt-3">
                <Button className="bg-red-700 hover:bg-red-800 text-white" onClick={() => handleBulkDeleteAssignments("Μονομελές")} disabled={bulkDeleting}
                  data-testid="bulk-delete-mon-btn">
                  <Trash2 className="w-4 h-4 mr-2" />Διαγραφή Όλων (Μονομελές)
                </Button>
                <Button className="bg-red-700 hover:bg-red-800 text-white" onClick={() => handleBulkDeleteAssignments("Πολυμελές")} disabled={bulkDeleting}
                  data-testid="bulk-delete-pol-btn">
                  <Trash2 className="w-4 h-4 mr-2" />Διαγραφή Όλων (Πολυμελές)
                </Button>
              </div>
              {bulkDeleteResult && <p className="text-sm text-red-800 mt-2 font-medium">{bulkDeleteResult}</p>}
            </div>

            {/* Email Notifications - Pretrial Reminders */}
            <div className="p-4 rounded-lg border bg-blue-50 border-blue-200">
              <h3 className="font-medium text-blue-900 flex items-center gap-2"><Mail className="w-4 h-4" />Ειδοποιήσεις Δικαστών - Προδικαστικός Έλεγχος</h3>
              <p className="text-sm text-blue-700 mt-1">Αποστολή email υπενθύμισης στους δικαστές για υποθέσεις με προθεσμία προδικαστικού ελέγχου εντός Χ ημερών</p>
              <div className="flex items-center gap-3 mt-3">
                <Label className="text-sm text-blue-800">Ημέρες πριν:</Label>
                <Input type="number" min="1" max="90" value={reminderDays} onChange={e => setReminderDays(parseInt(e.target.value) || 7)}
                  className="w-20" data-testid="reminder-days-input" />
                <Button className="bg-blue-600 hover:bg-blue-700 text-white" onClick={handleSendReminders} disabled={sendingReminders}
                  data-testid="send-reminders-btn">
                  {sendingReminders ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Αποστολή...</> : <><Send className="w-4 h-4 mr-2" />Αποστολή Υπενθυμίσεων</>}
                </Button>
              </div>
              {reminderResult && <p className="text-sm text-emerald-700 mt-2 font-medium">{reminderResult}</p>}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  return (
    <div className="space-y-6 animate-fadeIn">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Διαχείριση</h1>
        <p className="text-slate-500 mt-1">Χρήστες, ρυθμίσεις email, SMS και templates</p>
      </div>

      <Tabs defaultValue="users">
        <TabsList className="grid w-full grid-cols-9 bg-slate-100 h-11">
          <TabsTrigger value="users" className="text-slate-700 data-[state=active]:bg-white data-[state=active]:text-slate-900 data-[state=active]:shadow-sm" data-testid="admin-tab-users"><Users className="w-4 h-4 mr-2" />Χρήστες</TabsTrigger>
          <TabsTrigger value="case-delete" className="text-slate-700 data-[state=active]:bg-white data-[state=active]:text-red-600 data-[state=active]:shadow-sm" data-testid="admin-tab-case-delete"><Trash2 className="w-4 h-4 mr-2" />Υποθέσεις</TabsTrigger>
          <TabsTrigger value="smtp" className="text-slate-700 data-[state=active]:bg-white data-[state=active]:text-slate-900 data-[state=active]:shadow-sm" data-testid="admin-tab-smtp"><Mail className="w-4 h-4 mr-2" />SMTP</TabsTrigger>
          <TabsTrigger value="daily-digest" className="text-slate-700 data-[state=active]:bg-white data-[state=active]:text-slate-900 data-[state=active]:shadow-sm" data-testid="admin-tab-daily-digest"><Mail className="w-4 h-4 mr-2" />Daily Digest</TabsTrigger>
          <TabsTrigger value="sms" className="text-slate-700 data-[state=active]:bg-white data-[state=active]:text-slate-900 data-[state=active]:shadow-sm" data-testid="admin-tab-sms"><Phone className="w-4 h-4 mr-2" />SMS</TabsTrigger>
          <TabsTrigger value="templates" className="text-slate-700 data-[state=active]:bg-white data-[state=active]:text-slate-900 data-[state=active]:shadow-sm" data-testid="admin-tab-templates"><FileText className="w-4 h-4 mr-2" />Templates</TabsTrigger>
          <TabsTrigger value="pinakio-branches" className="text-slate-700 data-[state=active]:bg-white data-[state=active]:text-slate-900 data-[state=active]:shadow-sm" data-testid="admin-tab-pinakio-branches"><MapPin className="w-4 h-4 mr-2" />Πινάκια ↔ Καταστήματα</TabsTrigger>
          <TabsTrigger value="tools" className="text-slate-700 data-[state=active]:bg-white data-[state=active]:text-slate-900 data-[state=active]:shadow-sm" data-testid="admin-tab-tools"><Settings className="w-4 h-4 mr-2" />Εργαλεία</TabsTrigger>
          <TabsTrigger value="audit" className="text-slate-700 data-[state=active]:bg-white data-[state=active]:text-slate-900 data-[state=active]:shadow-sm" data-testid="admin-tab-audit"><History className="w-4 h-4 mr-2" />Ενέργειες</TabsTrigger>
        </TabsList>

        <TabsContent value="users" className="mt-4"><UsersTab /></TabsContent>
        <TabsContent value="case-delete" className="mt-4"><CaseDeletionTab /></TabsContent>
        <TabsContent value="smtp" className="mt-4"><SmtpTab /></TabsContent>
        <TabsContent value="daily-digest" className="mt-4"><DailyDigestPanel /></TabsContent>
        <TabsContent value="sms" className="mt-4"><SmsTab /></TabsContent>
        <TabsContent value="templates" className="mt-4"><TemplatesTab /></TabsContent>
        <TabsContent value="pinakio-branches" className="mt-4"><PinakioBranchMappingTab /></TabsContent>
        <TabsContent value="tools" className="mt-4"><ToolsTab /></TabsContent>
        <TabsContent value="audit" className="mt-4"><AuditLogTab /></TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminPanel;
